import { forwardRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";

type AddDatasetCardProps = {
  children: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
  dataLoc?: string;
};

type SubComponents = {
  Title: React.FC<{ children: React.ReactNode }>;
  Description: React.FC<{ children: React.ReactNode }>;
  Detail: React.FC<{ children: React.ReactNode; icon?: IconProp }>;
};

export const AddDatasetCard = forwardRef<
  HTMLButtonElement,
  AddDatasetCardProps
>(({ children, selected, onClick, dataLoc, ...rest }, ref) => {
  return (
    <button
      ref={ref}
      {...rest}
      aria-pressed={selected}
      className={twJoin(
        "group w-full cursor-pointer space-y-1 rounded-lg border px-4 py-3 text-left transition-all",
        "hover:border-indigo-300 hover:bg-indigo-25",
        !selected && "border-gray-200",
        selected && "border-indigo-200 bg-indigo-50",
      )}
      data-loc={dataLoc}
      data-selected={selected}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  );
}) as React.ForwardRefExoticComponent<
  AddDatasetCardProps & React.RefAttributes<HTMLButtonElement>
> &
  SubComponents;

AddDatasetCard.Title = ({ children }) => (
  <div className="font-inter-semibold-14px text-gray-800 group-data-[selected=true]:text-indigo-600">
    {children}
  </div>
);

AddDatasetCard.Description = ({ children }) => (
  <div className="font-inter-normal-12px text-gray-500">{children}</div>
);

AddDatasetCard.Detail = ({ children, icon }) => (
  <div className="flex items-center gap-1 font-inter-normal-12px text-gray-500 text-sm">
    {icon && <Icon icon={icon} size="xs" />}
    {children}
  </div>
);
